import React, { useState } from 'react';
import {
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { Controller, useFormContext, FieldErrors } from 'react-hook-form';
import { ParentRegisterData } from '../../../../../types/auth';

const ParentAccountStep = () => {
  const { control, formState: { errors }, watch } = useFormContext<ParentRegisterData>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = watch('parentData.password');

  // Properly typed errors
  const parentDataErrors = errors.parentData as FieldErrors<ParentRegisterData['parentData']> | undefined;

  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'secondary.main' }}>
        Parent Account Information
      </Typography>

      <Controller
        name="parentData.email"
        control={control}
        rules={{
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address'
          }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="normal"
            required
            fullWidth
            id="parentEmail"
            label="Parent Email Address"
            autoComplete="email"
            autoFocus
            error={!!parentDataErrors?.email}
            helperText={parentDataErrors?.email?.message}
            inputProps={{ 'data-testid': 'parent-email-input' }}
          />
        )}
      />

      <Controller
        name="parentData.password"
        control={control}
        rules={{
          required: 'Password is required',
          minLength: {
            value: 6,
            message: 'Password must be at least 6 characters'
          }
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="normal"
            required
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="parentPassword"
            error={!!parentDataErrors?.password}
            helperText={parentDataErrors?.password?.message}
            inputProps={{ 'data-testid': 'parent-password-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="parentData.confirmPassword"
        control={control}
        rules={{
          required: 'Please confirm your password',
          validate: (value) =>
            value === password || 'Passwords do not match'
        }}
        render={({ field }) => (
          <TextField
            {...field}
            margin="normal"
            required
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            id="parentConfirmPassword"
            error={!!parentDataErrors?.confirmPassword}
            helperText={parentDataErrors?.confirmPassword?.message}
            inputProps={{ 'data-testid': 'parent-confirmPassword-input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}
      />
    </>
  );
};

export default ParentAccountStep;
